import {createSlice} from '@reduxjs/toolkit'
import {toast} from 'react-toastify'
import ResourceService from '../../api/resource'
import Statistics from '../../api/statistics'
import {routes} from '../../routes'
import FormHelper from '../../utils/forms'
import statusCodes from '../../utils/http'
import localStorageManager from '../../utils/localStorageManager'
import {doNothing} from '../../utils/placeholder'
import NavigationHelper from '../../utils/navigation'

const logsSlice = createSlice({
    name: 'log_management',
    initialState: {
        logGroups: [],
        logGroup: {},
        isFetching: true,
        isLogGroupFetching: true,
        isButtonLoading: false
    },
    reducers: {
        setLogGroups: (state, {payload}) => {
            state.logGroups = payload
        },
        setLogGroup: (state, {payload}) => {
            state.logGroup = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setIsLogGroupFetching: (state, {payload}) => {
            state.isLogGroupFetching = payload
        },
        setIsButtonLoading: (state, {payload}) => {
            state.isButtonLoading = payload
        }
    }
})

export const {setLogGroups, setIsFetching, setLogGroup, setIsLogGroupFetching, setIsButtonLoading} = logsSlice.actions

export default logsSlice.reducer

export const getLogGroups = (showLoader = true, selectedService = null) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsLogGroupFetching(true))
        const services =  selectedService || localStorageManager.get('services')
        try {
            const responses = await Promise.all(services?.map(service => ResourceService.getLogGroups(service?.value)))
            const logGroups = responses.flatMap((res) =>
                res?.data?.data?.log_groups
                    ? res.data.data.log_groups
                    : []
            )
            dispatch(setLogGroups(logGroups))
            if (selectedService) {
                return logGroups
            }
        } catch {
            doNothing()
        } finally {
            showLoader && dispatch(setIsLogGroupFetching(false))
        }
    }
}

export const getLogGroup = (logGroupId, showLoader = true) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsLogGroupFetching(true))
        try {
            const {data} = await ResourceService.getLogGroup(logGroupId)
            dispatch(setLogGroup(data?.data?.log_group))
        } catch {
            doNothing()
        } finally {
            showLoader && dispatch(setIsLogGroupFetching(false))
        }
    }
}

export const getLogs = (query, navigate, showLoader = false, limit = 100) => {
    return async dispatch => {
        showLoader && dispatch(setIsFetching(true))
        try {
            const finalQuery = query ? query + `&limit=${limit}` : `?limit=${limit}`
            const {data} = await Statistics.getLogs(finalQuery)
            return ({
                data: data?.data?.logs || [],
                count: data?.meta?.count || '',
                fields: data?.meta?.fields || []
            })
        } catch (e) {
            if (statusCodes.SpecialErrors.includes(e?.response?.status)) {
                navigate(NavigationHelper.getLocalizedPath(routes.not_found.paths))
                return
            }
        } finally {
            dispatch(setIsFetching(false))
        }
    }
}

export const deleteLogGroup = (logGroupId) => {
    return async(dispatch) => {
        try {
            const {data} = await ResourceService.deleteLogGroup(logGroupId)
            toast.success(data?.message)
            dispatch(getLogGroups(false))
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const addLogGroup = (values, setFieldError) => {
    return async(dispatch) => {
        dispatch(setIsButtonLoading(true))
        try {
            const {data} = await ResourceService.addLogGroup(values)
            await dispatch(getLogGroups(false))
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}

export const updateLogGroup = (id, values, setFieldError) => {
    return async(dispatch) => {
        dispatch(setIsButtonLoading(true))
        try {
            const {data} = await ResourceService.updateLogGroup(id, values)
            await dispatch(getLogGroups(false))
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}
